// src/lib/resources/api/glossary/shared.ts

/**
 * @fileoverview Pure utilities for the glossary system, including type guards, feature checks, and data transformations.
 * @exports isMetricTerm - Type guard for MetricTerm
 * @exports isChartTerm - Type guard for ChartTerm
 * @exports isChartExample - Type guard for ChartExample
 * @exports isExampleWithContext - Type guard for ExampleWithContext
 * @exports isPlatformTerm - Type guard for PlatformTerm
 * @exports isCreativeTerm - Type guard for CreativeTerm
 * @exports hasKeyPoints - Feature check for terms with key points
 * @exports hasBestPractices - Feature check for terms with best practices
 * @exports hasBestUsedFor - Feature check for terms with best use cases
 * @exports hasSupplementalResources - Feature check for terms with supplemental resources
 * @exports getAvailableTabs - Get available tabs for a glossary term
 * @exports isStringListSection - Type guard for string list sections
 * @exports isKeyValueListSection - Type guard for key-value list sections
 * @exports isNestedListSection - Type guard for nested list sections
 * @exports isDefinitionListSection - Type guard for definition list sections
 * @exports isGroupedExamplesSection - Type guard for grouped examples sections
 * @exports parseStringListSection - Transform raw data into typed StringListSection
 * @exports parseDefinitionListSection - Transform raw data into typed DefinitionListSection
 * @exports parseGroupedExamplesSection - Transform raw data into typed GroupedExamplesSection
 * @exports parseKeyValueListSection - Transform raw data into typed KeyValueListSection
 * @exports parseNestedListSection - Transform raw data into typed NestedListSection
 * @exports detectContentType - Attempt to determine content type of raw data
 * @exports transformSectionContent - Transform raw section data based on expected type
 */

import type { 
  GlossaryTerm, 
  MetricTerm, 
  ChartTerm,
  PlatformTerm,
  ChartExample,
  ExampleWithContext,
  CreativeTerm
} from '~/lib/resources/types/glossary/terms'
import type { BaseGlossaryTerm, SupplementalResourceReference } from '~/lib/resources/types/shared/base'
import type { TabType } from '~/lib/resources/types/glossary/components'
import type {
  ContentSectionTypes,
  StringListSection,
  KeyValueListSection,
  NestedListSection,
  DefinitionListSection,
  GroupedExamplesSection,
  NameDescriptionPair,
  NameWithExamples
} from '~/lib/resources/types/glossary/field-types'
import { ContentType } from '~/lib/resources/types/glossary/field-types'

// Glossary term heading — shared between the SSR fallback
// (GlossaryTermServerContent) and the client-enhanced layout
// (GlossaryTermLayout). Both surfaces must render the same h1 text and
// styling so the post-hydration swap doesn't produce a visible reflow
// (CLS) or a Speakable/crawl-vs-render text mismatch. Centralising
// resolution + className here makes the lockstep mechanical rather
// than a convention re-derived in each component.
//
// The textual rule (canonicalTitle ?? title) matches the convention
// already used by every other glossary surface (GlossaryAlphaIndex,
// GlossaryTermGrid, related-term renderers, BenchmarksTable's
// metricName prop). The SSR h1 was historically using raw term.title;
// this helper closes that gap so both surfaces resolve identically.

export const GLOSSARY_HEADING_TITLE_CLASSNAME =
  'text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl'

export function getGlossaryHeadingTitle(
  term: Pick<GlossaryTerm, 'canonicalTitle' | 'title'>,
  fallback = 'Untitled Term'
): string {
  const raw = term.canonicalTitle ?? term.title ?? ''
  const trimmed = typeof raw === 'string' ? raw.trim() : ''
  return trimmed || fallback
}

// Type Guards

/**
 * Type guard for MetricTerm
 * 
 * Determines if a glossary term is specifically a metric term by checking for required
 * properties in the calculation object.
 * 
 * @param {GlossaryTerm} value - The glossary term to check
 * @returns {boolean} True if the term is a valid MetricTerm, false otherwise
 * 
 * @example
 * if (isMetricTerm(term)) {
 *   // Can safely access term.calculation.formula
 *   console.log(term.calculation.formula)
 * }
 */
export function isMetricTerm(value: GlossaryTerm): value is (BaseGlossaryTerm & MetricTerm & { category: 'metrics' }) {
  return value.category === 'metrics' && 
         'calculation' in value &&
         typeof value.calculation === 'object' &&
         value.calculation !== null &&
         'operation' in value.calculation &&
         'formula' in value.calculation &&
         'explanation' in value.calculation &&
         'components' in value.calculation &&
         Array.isArray(value.calculation.components)
}

/**
 * Type guard for ChartTerm
 * 
 * Validates if a glossary term is a chart term by checking for the required
 * dataRequirements object and its properties.
 * 
 * @param {GlossaryTerm} value - The glossary term to check
 * @returns {boolean} True if the term is a valid ChartTerm, false otherwise
 * 
 * @example
 * if (isChartTerm(term)) {
 *   // Can safely access term.dataRequirements
 *   const format = term.dataRequirements.format
 * }
 */
export function isChartTerm(value: GlossaryTerm): value is (BaseGlossaryTerm & ChartTerm & { category: 'charts' }) {
  return value.category === 'charts' &&
         'dataRequirements' in value &&
         typeof value.dataRequirements === 'object' &&
         value.dataRequirements !== null &&
         'format' in value.dataRequirements &&
         'structure' in value.dataRequirements
}

/**
 * Type guard for ChartExample
 * 
 * Validates if an object is a chart example by checking for required properties
 * including data, config, and description.
 * 
 * @param {unknown} example - The object to check
 * @returns {boolean} True if the object is a valid ChartExample, false otherwise
 * 
 * @example
 * const examples = term.examples || []
 * const chartExamples = examples.filter(isChartExample)
 */
export function isChartExample(example: unknown): example is ChartExample {
  if (typeof example !== 'object' || example === null) return false
  const obj = example as Record<string, unknown>
  // Validate value shapes (`data` must be an array of records, `config`
  // must be a non-null object, `description` must be a string) so a YAML
  // entry with `data: null` doesn't pass the guard then crash inside
  // downstream renderers expecting a real shape.
  return (
    Array.isArray(obj.data) &&
    typeof obj.config === 'object' &&
    obj.config !== null &&
    typeof obj.description === 'string'
  )
}

/**
 * Type guard for ExampleWithContext
 * 
 * Determines if an object is an example with contextual information by checking
 * for the required text and context properties.
 * 
 * @param {unknown} example - The object to check
 * @returns {boolean} True if the object is a valid ExampleWithContext, false otherwise
 * 
 * @example
 * const examples = term.examples || []
 * const contextualExamples = examples.filter(isExampleWithContext)
 * if (contextualExamples.length > 0) {
 *   console.log(contextualExamples[0].context)
 * }
 */
export function isExampleWithContext(example: unknown): example is ExampleWithContext {
  return (
    typeof example === 'object' &&
    example !== null &&
    'text' in example &&
    'context' in example &&
    typeof (example as ExampleWithContext).text === 'string' &&
    typeof (example as ExampleWithContext).context === 'string'
  )
}

/**
 * Type guard for PlatformTerm
 * 
 * Validates if a glossary term is a platform-specific term by checking the category
 * and validating optional platform-specific properties.
 * 
 * @param {GlossaryTerm} value - The glossary term to check
 * @returns {boolean} True if the term is a valid PlatformTerm, false otherwise
 * 
 * @example
 * if (isPlatformTerm(term)) {
 *   // Can safely access platform-specific properties
 *   const platforms = term.platformName || []
 * }
 */
export function isPlatformTerm(value: GlossaryTerm): value is (BaseGlossaryTerm & PlatformTerm & { category: 'platform' }) {
  return value.category === 'platform' && 
         (!('platformName' in value) || Array.isArray(value.platformName)) &&
         (!('examples' in value) || Array.isArray(value.examples))
}

/**
 * Type guard for CreativeTerm
 * 
 * Determines if a glossary term is a creative term by checking the category
 * and validating that it contains an examples array.
 * 
 * @param {GlossaryTerm} value - The glossary term to check
 * @returns {boolean} True if the term is a valid CreativeTerm, false otherwise
 * 
 * @example
 * if (isCreativeTerm(term)) {
 *   // Can safely access creative-specific properties
 *   const examples = term.examples
 * }
 */
export function isCreativeTerm(value: GlossaryTerm): value is (BaseGlossaryTerm & CreativeTerm & { category: 'creative' }) {
  return value.category === 'creative' &&
         'examples' in value &&
         Array.isArray(value.examples)
}

// Feature checks

/**
 * Checks if a glossary term has key points
 * 
 * This type guard validates that the term contains a non-empty array of key points,
 * which are important highlights about the term.
 * 
 * @param {GlossaryTerm} term - The glossary term to check
 * @returns {boolean} True if the term has valid key points, false otherwise
 * 
 * @example
 * if (hasKeyPoints(term)) {
 *   // term.keyPoints is guaranteed to be a non-empty array
 *   return <KeyPointsList points={term.keyPoints} />
 * }
 */
export function hasKeyPoints(term: GlossaryTerm): term is GlossaryTerm & { keyPoints: string[] } {
  return Array.isArray(term.keyPoints) && term.keyPoints.length > 0
}

/**
 * Checks if a glossary term has best practices
 * 
 * This type guard validates that the term contains a non-empty array of best practices,
 * which provide guidance on optimal usage of the concept.
 * 
 * @param {GlossaryTerm} term - The glossary term to check
 * @returns {boolean} True if the term has valid best practices, false otherwise
 * 
 * @example
 * if (hasBestPractices(term)) {
 *   // term.bestPractices is guaranteed to be a non-empty array
 *   return <BestPracticesList practices={term.bestPractices} />
 * }
 */
export function hasBestPractices(term: GlossaryTerm): term is GlossaryTerm & { bestPractices: string[] } {
  return Array.isArray(term.bestPractices) && term.bestPractices.length > 0
}

/**
 * Checks if a glossary term has best use cases
 * 
 * This type guard validates that the term contains a non-empty array of best use cases,
 * which describe optimal scenarios for applying the concept.
 * 
 * @param {GlossaryTerm} term - The glossary term to check
 * @returns {boolean} True if the term has valid best use cases, false otherwise
 * 
 * @example
 * if (hasBestUsedFor(term)) {
 *   // term.bestUsedFor is guaranteed to be a non-empty array
 *   return <UseCasesList cases={term.bestUsedFor} />
 * }
 */
export function hasBestUsedFor(term: GlossaryTerm): term is GlossaryTerm & { bestUsedFor: string[] } {
  return Array.isArray(term.bestUsedFor) && term.bestUsedFor.length > 0
}

/**
 * Checks if a glossary term has supplemental resources
 * 
 * This replaces the deprecated hasExternalResources() function by checking for the new
 * supplementalResources field that combines both internal and external resources.
 * 
 * This type guard validates that the term contains a non-empty array of supplemental resources,
 * which can include both internal and external references for further reading.
 * 
 * @param {GlossaryTerm} term - The glossary term to check
 * @returns {boolean} True if the term has valid supplemental resources, false otherwise
 * 
 * @example
 * if (hasSupplementalResources(term)) {
 *   // term.supplementalResources is guaranteed to be a non-empty array
 *   const resources = term.supplementalResources
 * }
 * 
 * @important This replaces the deprecated hasExternalResources() function by checking for the new
 * supplementalResources field that combines both internal and external resources.
 */
export function hasSupplementalResources(term: GlossaryTerm): term is GlossaryTerm & { supplementalResources: SupplementalResourceReference[] } {
  return 'supplementalResources' in term && 
         Array.isArray(term.supplementalResources) && 
         term.supplementalResources.length > 0
}

// Pure utility functions

/**
 * Determines available tabs for a glossary term based on its content
 * 
 * Analyzes a glossary term's properties to determine which tabs should be displayed
 * in the UI. The function checks for the presence of various content types and
 * returns an array of tab identifiers.
 * 
 * This is a pure function that accepts data as parameters and performs no I/O operations.
 * 
 * @param {GlossaryTerm} term - The glossary term to analyze
 * @returns {TabType[]} Array of available tab types for the term
 * 
 * @example
 * const tabs = getAvailableTabs(term)
 * return (
 *   <TabNavigation tabs={tabs} activePath={activeTab} baseUrl={`/resources/glossary/${term.category}/${term.slug}`} />
 * );
 */
export function getAvailableTabs(term: GlossaryTerm): TabType[] { 
  const tabs: TabType[] = ['definition'] // Always include definition tab

  // Add examples tab if valid examples exist
  if (Array.isArray(term.examples) && term.examples.length > 0 && term.examples.some(example => {
    if (typeof example === 'string') return example.trim().length > 0
    if (isExampleWithContext(example)) return example.text.trim().length > 0
    if (isChartExample(example)) return example.description.trim().length > 0
    return false
  })) {
    tabs.push('examples')
  }

  // Add metric-specific tabs
  if (isMetricTerm(term)) {
    if (term.calculation) {
      tabs.push('calculation')
    }
    if (term.relatedTerms && term.relatedTerms.length > 0) {
      tabs.push('comparison')
    }
  }

  // Add chart-specific tabs
  if (isChartTerm(term)) {
    if (term.bestUsedFor?.length > 0) {
      tabs.push('usage')
    }
    if (term.limitations?.length > 0) {
      tabs.push('limitations')
    }
  }

  // Add additional tabs based on content
  if (hasBestPractices(term) && term.bestPractices?.length > 0) {
    tabs.push('bestPractices')
  }

  // Add external resources tab if term has supplemental resources
  if (hasSupplementalResources(term)) {
    tabs.push('supplementalResources')
  }

  if (hasBestUsedFor(term) && term.bestUsedFor?.length > 0) {
    tabs.push('bestUsedFor')
  }

  if (hasKeyPoints(term) && term.keyPoints?.length > 0) {
    tabs.push('keyPoints')
  }

  // Add tabs for dynamic field content
  if (term.fieldContent) {
    for (const [fieldName, content] of Object.entries(term.fieldContent)) {
      // Skip fields that already have tabs
      if (tabs.includes(fieldName as TabType)) continue

      // Add tab if content exists and is valid
      if (content && 'type' in content && 'items' in content && content.items.length > 0) {
        tabs.push(fieldName as TabType)
      }
    }
  }

  return tabs
}

/**
 * Type guard for string list sections
 * 
 * @param {ContentSectionTypes} section - The content section to check
 * @returns {boolean} True if the section is a string list
 */
export function isStringListSection(
  section: ContentSectionTypes
): section is StringListSection {
  return section.type === ContentType.STRING_LIST
}

/**
 * Type guard for key-value list sections
 * 
 * @param {ContentSectionTypes} section - The content section to check
 * @returns {boolean} True if the section is a key-value list
 */
export function isKeyValueListSection(
  section: ContentSectionTypes
): section is KeyValueListSection<Record<string, string>> {
  return section.type === ContentType.KEY_VALUE_LIST
}

/**
 * Type guard for nested list sections
 * 
 * @template MainKey - The expected main key type
 * @template ListKey - The expected list key type
 * @param {ContentSectionTypes} section - The content section to check
 * @returns {boolean} True if the section is a nested list
 */
export function isNestedListSection<MainKey extends string, ListKey extends string>(
  section: ContentSectionTypes
): section is NestedListSection<MainKey, ListKey> {
  return section.type === ContentType.NESTED_LIST
}

/**
 * Type guard specifically for definition lists (name/description)
 * Validates both type and key structure
 * 
 * @param {ContentSectionTypes} section - The content section to check
 * @returns {boolean} True if the section is a definition list
 */
export function isDefinitionListSection(
  section: ContentSectionTypes
): section is DefinitionListSection {
  return (
    section.type === ContentType.KEY_VALUE_LIST &&
    'keyInfo' in section &&
    section.keyInfo.primaryKey === 'name' &&
    section.keyInfo.secondaryKey === 'description'
  )
}

/**
 * Type guard specifically for grouped examples sections (name/examples)
 * Validates both type and key structure
 * 
 * @param {ContentSectionTypes} section - The content section to check
 * @returns {boolean} True if the section is a grouped examples list
 */
export function isGroupedExamplesSection(
  section: ContentSectionTypes
): section is GroupedExamplesSection {
  return (
    section.type === ContentType.NESTED_LIST &&
    'keyInfo' in section &&
    section.keyInfo.mainKey === 'name' &&
    section.keyInfo.listKey === 'examples'
  )
}

/**
 * Transforms raw data into typed StringListSection.
 * 
 * @param {unknown} data - The raw data to transform
 * @param {string} sectionName - The name of the section
 * @returns {StringListSection | null} Typed string list section or null if invalid
 */
export function parseStringListSection(data: unknown, sectionName: string): StringListSection | null {
  if (!Array.isArray(data) || !data.every(item => typeof item === 'string')) {
    return null
  }
  
  return {
    type: ContentType.STRING_LIST,
    sectionName,
    items: data,
    display: {
      component: 'simple-list',
      layout: { compact: true }
    }
  }
}

/**
 * Transforms raw data into typed DefinitionListSection.
 * 
 * @param {unknown} data - The raw data to transform
 * @param {string} sectionName - The name of the section
 * @returns {DefinitionListSection | null} Typed definition list section or null if invalid
 */
export function parseDefinitionListSection(data: unknown, sectionName: string): DefinitionListSection | null {
  if (!Array.isArray(data)) return null
  
  const isValid = data.every(item => {
    if (!item || typeof item !== 'object' || item === null) return false
    
    const typedItem = item as Record<string, unknown>
    return (
      'name' in typedItem && 
      typeof typedItem.name === 'string' &&
      'description' in typedItem && 
      typeof typedItem.description === 'string'
    )
  })
  
  if (!isValid) return null
  
  return {
    type: ContentType.KEY_VALUE_LIST,
    sectionName,
    items: data as ReadonlyArray<NameDescriptionPair>,
    keyInfo: {
      primaryKey: 'name',
      secondaryKey: 'description',
      allKeys: ['name', 'description']
    },
    display: {
      component: 'definition-list',
      layout: { columns: 1 }
    }
  }
}

/**
 * Transforms raw data into typed GroupedExamplesSection.
 * 
 * @param {unknown} data - The raw data to transform
 * @param {string} sectionName - The name of the section
 * @returns {GroupedExamplesSection | null} Typed grouped examples section or null if invalid
 */
export function parseGroupedExamplesSection(data: unknown, sectionName: string): GroupedExamplesSection | null {
  if (!Array.isArray(data)) return null
  
  const isValid = data.every(item => {
    if (!item || typeof item !== 'object' || item === null) return false
    
    const typedItem = item as Record<string, unknown>
    return (
      'name' in typedItem && 
      typeof typedItem.name === 'string' &&
      'examples' in typedItem && 
      Array.isArray(typedItem.examples) &&
      typedItem.examples.every((example: unknown) => typeof example === 'string')
    )
  })
  
  if (!isValid) return null
  
  return {
    type: ContentType.NESTED_LIST,
    sectionName,
    items: data as ReadonlyArray<NameWithExamples>,
    keyInfo: {
      mainKey: 'name',
      listKey: 'examples'
    },
    display: {
      component: 'grouped-list',
      layout: { nestedStyle: 'indented' }
    }
  }
}

/**
 * Transforms raw data into typed KeyValueListSection.
 * 
 * @param {unknown} data - The raw data to transform
 * @param {string} sectionName - The name of the section
 * @param {string} primaryKey - The field name to use as the primary key
 * @param {string} secondaryKey - The field name containing the secondary values
 * @returns {KeyValueListSection<Record<string, string>> | null} Typed key-value list section or null if invalid
 */
export function parseKeyValueListSection(
  data: unknown,
  sectionName: string,
  primaryKey: string,
  secondaryKey: string
): KeyValueListSection<Record<string, string>> | null {
  if (!Array.isArray(data)) return null
  
  const isValid = data.every(item => {
    if (!item || typeof item !== 'object' || item === null) return false
    
    const typedItem = item as Record<string, unknown>
    return (
      primaryKey in typedItem && 
      typeof typedItem[primaryKey] === 'string' &&
      secondaryKey in typedItem && 
      (typeof typedItem[secondaryKey] === 'string' || 
       (Array.isArray(typedItem[secondaryKey]) && 
        typedItem[secondaryKey].every((val: unknown) => typeof val === 'string')))
    )
  })
  
  if (!isValid) return null
  
  return {
    type: ContentType.KEY_VALUE_LIST,
    sectionName,
    items: data as ReadonlyArray<Record<string, string>>,
    keyInfo: {
      primaryKey,
      secondaryKey,
      allKeys: [primaryKey, secondaryKey]
    },
    display: {
      component: 'key-value-list',
      layout: { columns: 1 }
    }
  }
}

/**
 * Transforms raw data into typed NestedListSection.
 * 
 * @param {unknown} data - The raw data to transform
 * @param {string} sectionName - The name of the section
 * @param {string} mainKey - The field name to use as the main key
 * @param {string} listKey - The field name containing the list values
 * @returns {NestedListSection<string, string> | null} Typed nested list section or null if invalid
 */
export function parseNestedListSection(
  data: unknown,
  sectionName: string,
  mainKey: string,
  listKey: string
): NestedListSection<string, string> | null {
  if (!Array.isArray(data)) return null
  
  const isValid = data.every(item => {
    if (!item || typeof item !== 'object' || item === null) return false
    
    const typedItem = item as Record<string, unknown>
    return (
      mainKey in typedItem && 
      typeof typedItem[mainKey] === 'string' &&
      listKey in typedItem && 
      Array.isArray(typedItem[listKey]) &&
      typedItem[listKey].every((val: unknown) => typeof val === 'string')
    )
  })
  
  if (!isValid) return null
  
  // Create a properly typed record with specific keys for main and list values
  type NestedItem = Record<string, string> & Record<string, ReadonlyArray<string>>
  const typedItems = data as ReadonlyArray<NestedItem>
  
  return {
    type: ContentType.NESTED_LIST,
    sectionName,
    items: typedItems,
    keyInfo: {
      mainKey,
      listKey
    },
    display: {
      component: 'nested-list',
      layout: { nestedStyle: 'indented' }
    }
  }
}

/**
 * Attempts to determine the content type of raw data.
 * This is useful when you don't know the expected type in advance.
 * 
 * @param {unknown} data - The raw data to analyze
 * @param {string} sectionName - The name of the section
 * @returns {ContentSectionTypes | null} The detected content type or null if unrecognized
 */
export function detectContentType(data: unknown, sectionName: string): ContentSectionTypes | null {
  // Try string list first (simplest case)
  const stringList = parseStringListSection(data, sectionName)
  if (stringList) return stringList
  
  // If it's an array of objects, check various patterns
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null) {
    const firstItem = data[0] as Record<string, unknown>
    
    // Check for name-description pattern
    const nameDesc = parseDefinitionListSection(data, sectionName)
    if (nameDesc) return nameDesc
    
    // Check for name-examples pattern
    const nameExamples = parseGroupedExamplesSection(data, sectionName)
    if (nameExamples) return nameExamples
    
    // Check for platform-features pattern (common case)
    if ('platform' in firstItem) {
      const valueField = 'features' in firstItem ? 'features' : 
                         'examples' in firstItem ? 'examples' : null
      if (valueField) {
        const nestedList = parseNestedListSection(data, sectionName, 'platform', valueField)
        if (nestedList) return nestedList
      }
    }
  }
  
  return null
}

/**
 * Transforms raw section data based on expected type or auto-detection.
 * 
 * @param {string} sectionName - The name of the section
 * @param {unknown} data - The raw section data
 * @param {typeof ContentType[keyof typeof ContentType]} [expectedType] - The expected content type
 * @returns {ContentSectionTypes | null} Typed section content or null if invalid
 */
export function transformSectionContent(
  sectionName: string,
  data: unknown,
  expectedType?: typeof ContentType[keyof typeof ContentType]
): ContentSectionTypes | null {
  if (!data) return null
  
  // If no expected type, try to detect the type
  if (!expectedType) {
    return detectContentType(data, sectionName)
  }
  
  // Apply transformations based on expected type
  switch (expectedType) {
    case ContentType.STRING_LIST:
      return parseStringListSection(data, sectionName)
      
    case ContentType.KEY_VALUE_LIST: {
      // First try name-description format
      const nameDesc = parseDefinitionListSection(data, sectionName)
      if (nameDesc) return nameDesc
      
      // If that fails, try to determine key structure
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null) {
        const firstItem = data[0] as Record<string, unknown>
        const keys = Object.keys(firstItem)
        if (keys.length >= 2) {
          // Ensure we have valid keys before passing them
          const primaryKey = keys[0]
          const secondaryKey = keys[1]
          if (primaryKey && secondaryKey) {
            return parseKeyValueListSection(data, sectionName, primaryKey, secondaryKey)
          }
        }
      }
      
      return null
    }
      
    case ContentType.NESTED_LIST: {
      // First try name-examples format
      const nameExamples = parseGroupedExamplesSection(data, sectionName)
      if (nameExamples) return nameExamples
      
      // If that fails, try to determine key structure
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null) {
        const firstItem = data[0] as Record<string, unknown>
        const keys = Object.keys(firstItem)
        
        // Find a key that has an array value
        for (const key of keys) {
          if (Array.isArray(firstItem[key])) {
            // Ensure we have a valid main key
            const mainKey = keys.find(k => k !== key)
            if (mainKey) {
              return parseNestedListSection(data, sectionName, mainKey, key)
            }
          }
        }
      }
      
      return null
    }
      
    default: {
      return null
    }
  }
}